// Recognizing the same document again.
//
// CONSUMER: shared (web prep screen + Workers routes + tests).
//
// Grant, 2026-08-22: the feature "should learn where users like to place
// signature spots… and get better at suggesting." Learning is worthless
// without recognition, and recognition is the whole difficulty: the same
// contract, re-exported, is a completely different FILE.
//
// So the fingerprint is taken over the document's TEXT, not its bytes.
// Producer strings, creation timestamps, object ordering and compression
// all change between exports of an identical contract; the words on the
// page do not. Same reasoning as ivans_files.content_sha256.
//
// Pure and dependency-free (the field-types.ts rule) so the web bundle,
// the Worker and the node-pool tests all compute it the SAME way — a
// fingerprint that differs by caller recognizes nothing.

/** How much of the document goes into the fingerprint.
 *
 *  DECISION: the first pages only, for a specific reason rather than
 *  economy. Long contracts often carry a filled-in schedule or exhibit
 *  at the back that differs per client — hashing the whole document
 *  would make every copy of the same form look unique, which is exactly
 *  the failure that leaves the feature quietly never firing. The front
 *  matter is the part that identifies the FORM. */
const FINGERPRINT_PAGES = 3;

/** Guard against a pathological run of text making the hash input huge.
 *  Well past any real first-three-pages of a contract. */
const MAX_FINGERPRINT_CHARS = 20_000;

/** Fewest real words (letters only; the "#" a blank collapses to does not
 *  count) a document needs before it can be recognized. */
const MIN_FINGERPRINT_WORDS = 12;

export interface FingerprintSpan {
  pageNumber: number;
  text: string;
}

/**
 * The text that gets hashed, normalized so cosmetically-different
 * exports of one document agree.
 *
 * Exported separately from the hash so a test can assert what is being
 * compared — the normalization IS the behavior here, and a silent change
 * to it invalidates every layout the team has saved.
 */
export function fingerprintInput(spans: FingerprintSpan[]): string {
  const text = spans
    .filter((s) => s.pageNumber <= FINGERPRINT_PAGES)
    // Reading order is already the extraction order; sorting by page
    // keeps it stable if a caller ever collects pages out of order.
    .sort((a, b) => a.pageNumber - b.pageNumber)
    .map((s) => s.text)
    .join(" ")
    // Case and whitespace vary with the exporter, not the content.
    .toLowerCase()
    .replace(/\s+/g, " ")
    // Punctuation that PDF exporters render inconsistently: smart vs
    // straight quotes, en/em dashes, non-breaking spaces.
    //
    // EVERY quote goes to one character, not just the smart ones —
    // converting “ to ' while leaving " alone makes the two exports
    // differ in the other direction, which is what the test caught.
    .replace(/["'‘’“”]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/ /g, " ")
    // Everything that is "a value somebody fills in" collapses to one
    // marker, so a BLANK form and the same form with a price, a date or
    // a policy number typed into it fingerprint alike. Without this,
    // every send of one contract teaches a new layout and nothing is
    // ever recognized twice — which reads as "the feature isn't very
    // good" rather than as a bug. Found by the blank-vs-filled test.
    //
    // Three steps, and all three are needed:
    //   digits        450000     → #
    //   ruled blanks  ______     → #      (the blank form's version)
    //   grouped runs  #,# / #/#/# → #     ($450,000 and 08/23/2026 are
    //                                      one value, not three)
    .replace(/\d+/g, "#")
    .replace(/_{2,}/g, "#")
    .replace(/#(?:\s*[,./-]\s*#)+/g, "#")
    .trim();
  return text.slice(0, MAX_FINGERPRINT_CHARS);
}

/**
 * The fingerprint itself. Returns null when there is too little text to
 * identify anything — a scan, or a near-empty page — because a hash of
 * three words would collide across unrelated documents and start
 * applying one form's layout to another.
 */
export async function documentFingerprint(
  spans: FingerprintSpan[],
): Promise<string | null> {
  const input = fingerprintInput(spans);
  // DECISION (2026-10-05): the floor counts real WORDS, not characters.
  // It was 200 characters, which refused Baker's one-sentence Statement
  // of Completion (~170) with "no readable text" even though the text
  // was plainly there. A short acknowledgment form is a legitimate
  // document; what must stay out is a scan with a stray OCR artifact,
  // which has a handful of words at most.
  const words = input.match(/[a-z]{2,}/g)?.length ?? 0;
  if (words < MIN_FINGERPRINT_WORDS) return null;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
